import {
  Box,
  Button,
  Checkbox,
  Flex,
  Grid,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { schemaResolver } from '@mantine/form';
import { IconCalendar } from '@tabler/icons-react';
import { useLocation, useParams } from 'react-router-dom';
import { MessageImageUpload, wrapImageUploadProps } from './MessageImageUpload';
import { useEffect, useRef } from 'react';

import {
  MessageFormValue,
  ValidMessageFormValue,
  ValidMessageFormValueSchema,
  createMessageInitFormValues,
} from '#/components/ui/messages/model';

import { DateTimePicker } from '@mantine/dates';
import { MessageEditorFormProvider, useMessageEditorForm } from './context.ts';
import { SelectCategory } from './SelectCategory.tsx';
import { ActionEditor } from './actionEditor/ActionEditor.tsx';
import { TargetSelector } from './TargetSelector.tsx';
import { TemplatePreview } from './TemplatePreview.tsx';

const INIT_VALUES = createMessageInitFormValues();

export const MessageEditor = ({
  initialValues,
  handleSubmit,
}: {
  initialValues: MessageFormValue | undefined;
  handleSubmit: (p: ValidMessageFormValue) => void;
}) => {
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  const phaseLabel = (() => {
    if (!id) return '新增訊息';
    if (location.pathname.endsWith('/edit')) return '編輯訊息';
    return '檢視訊息';
  })();

  const formRef = useRef<HTMLFormElement>(null);

  const form = useMessageEditorForm({
    mode: 'controlled',
    initialValues: INIT_VALUES,
    validate: schemaResolver(ValidMessageFormValueSchema, { sync: true }),
    validateInputOnChange: true,
  });

  const isFormDirty = form.isDirty();

  useEffect(() => {
    if (!isFormDirty && initialValues) {
      form.setValues(initialValues as unknown as ValidMessageFormValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues, isFormDirty]);

  const imageUploadOptions = wrapImageUploadProps({
    value: form.getValues().thumbUrl,
    setValue: (v) =>
      form.setFieldValue(
        'thumbUrl',
        v as unknown as ValidMessageFormValue['thumbUrl'],
      ),
  });

  return (
    <>
      <MessageEditorFormProvider form={form}>
        <Paper radius="md" p="xl" withBorder className="flex-1">
          <Stack gap="lg" className="h-full">
            <Title order={2}>{phaseLabel}</Title>
            <Box className="flex-1">
              <form
                onSubmit={form.onSubmit(
                  (value) => {
                    handleSubmit(value);
                  },
                  (error) => {
                    console.warn(error);
                  },
                )}
                ref={formRef}
              >
                <Stack gap="lg">
                  <Box>
                    <Text fw={700} c="gray.8" mb="xs">
                      訊息內容設定
                    </Text>
                    <Grid>
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <Flex direction="column" gap="md">
                          <TextInput
                            label="名稱"
                            withAsterisk
                            key={form.key('name')}
                            {...form.getInputProps('name')}
                          />
                          <SelectCategory />

                          <TextInput
                            label="標題"
                            withAsterisk
                            placeholder="請輸入標題，建議 20 字內。"
                            key={form.key('title')}
                            {...form.getInputProps('title')}
                          />

                          <Textarea
                            label="內文"
                            withAsterisk
                            placeholder="請輸入內文，建議 50 字內。"
                            minRows={3}
                            autosize
                            key={form.key('summary')}
                            {...form.getInputProps('summary')}
                          />
                          <Box>
                            <Text size="sm" fw={500} mb={6}>
                              圖片（比例限制 1:1）
                            </Text>
                            <MessageImageUpload
                              {...imageUploadOptions}
                              error={form.errors['thumbUrl.kind']}
                            />
                          </Box>
                        </Flex>
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 6 }}>
                        <Stack>
                          <ActionEditor />
                          <TemplatePreview value={form.getValues()} />
                        </Stack>
                      </Grid.Col>
                    </Grid>
                  </Box>

                  <Box>
                    <Text fw={700} c="gray.8" mb="xs">
                      發送設定
                    </Text>
                    <Stack>
                      <Grid gap="md">
                        <Grid.Col span={{ base: 12, md: 6 }}>
                          <Group>
                            <Checkbox
                              label="發送推播通知"
                              key={form.key(
                                'notificationSettings.notification.push_enabled',
                              )}
                              {...form.getInputProps(
                                'notificationSettings.notification.push_enabled',
                              )}
                              checked={
                                form.getValues().notificationSettings
                                  .notification.push_enabled
                              }
                            />
                          </Group>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                          <DateTimePicker
                            valueFormat="YYYY MMM DD, hh:mm A"
                            label="發送時間"
                            withAsterisk
                            minDate={new Date()}
                            placeholder="請選擇發送時間"
                            leftSection={<IconCalendar size={16} />}
                            key={form.key('scheduleAt')}
                            timePickerProps={{
                              withDropdown: true,
                              popoverProps: { withinPortal: false },
                              format: '12h',
                            }}
                            {...form.getInputProps('scheduleAt')}
                            onChange={(value) => {
                              if (!value) {
                                return;
                              }

                              const newDate = new Date(value);
                              form.setFieldValue('scheduleAt', newDate);
                            }}
                          />
                        </Grid.Col>
                      </Grid>
                      <TargetSelector />
                    </Stack>
                  </Box>
                  <Group justify="flex-end">
                    <Button type="submit">送出</Button>
                  </Group>
                </Stack>
              </form>
            </Box>
          </Stack>
        </Paper>
      </MessageEditorFormProvider>
    </>
  );
};

export default MessageEditor;
